// 🔑 LOX LEVEL 2 — STAFF-ONLY CREDIT FLAGS ON THE MODULE'S CREDIT STEP (Jesse, 2026-10-29 — push C item 6).
// Each flag: the rule's AFM v2 cite (backend constants/loxRuleSet), what was found, and a suggested follow-up staff may
// edit and SEND (the existing staff → borrower message path, POST /api/admin/messages/send, then the flag records the
// message) or DISCARD with a reason. A flag never rejects, never holds and never messages on its own; the borrower
// never sees it (backend utils/creditRuleFlags).
// 2026-11-01 (Jesse): residual income is NET, estimated from federal tables — the working shows under the flag (X2); the
// household-size follow-up is held until counsel answers, so a held flag offers no follow-up (X3); a follow-up that
// repeats a step-7 letter arrives suppressed, with the reason (X4 A).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { T, card, label, btn } from './GuidedModuleSteps';

const input = { minHeight: 40, padding: 10, borderRadius: 8, border: `1px solid ${T.ink}`, font: `500 14px ${T.font}`, color: T.ink, boxSizing: 'border-box', width: '100%' };
const STATUS_LABEL = { open: 'Open', sent: 'Follow-up sent', discarded: 'Follow-up discarded', suppressed: 'Follow-up suppressed' };
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

// 🔑 X2: the residual-income working, net of estimated federal tax (backend utils/netIncomeEstimate).
function ResidualWorking({ r }) {
  if (!r || !r.net) return null;
  const n = r.net;
  return (
    <div data-testid="uw-credit-rule-flag-residual" style={{ fontSize: 13, lineHeight: 1.6, background: T.slateLight, borderRadius: 8, padding: '8px 10px' }}>
      <b>Residual income, net — estimated from federal tables (single-filer brackets)</b>
      <div>Gross income {money(n.grossMonthly)} − federal income tax {money(n.incomeTaxMonthly)} − payroll tax {money(n.payrollMonthly)} = net {money(n.netMonthly)} a month</div>
      <div>Net {money(n.netMonthly)} − housing {money(r.housingMonthly)} − debts {money(r.debtsMonthly)} = {r.left == null ? 'not computed' : `${money(r.left)} left`}{r.required != null ? ` vs ${money(r.required)} required` : ''}</div>
      <div style={{ fontSize: 12 }}>{n.taxYear} tables · single-filer brackets, each applicant on their own income · standard deduction, no credits, no state tax</div>
    </div>
  );
}

export default function CreditRuleFlags({ API, borrowerId, headers, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});        // flag id → the text to send
  const [discard, setDiscard] = useState({});  // flag id → the reason being typed (present = the box is open)
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/credit-rule-flags`, { headers }); setData(r.data || null); }
    catch (e) { setErr(e?.response?.data?.error || 'The credit flags could not be read just now.'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, borrowerId, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const send = async (f) => {
    setErr(null); setBusy(f.id);
    const body = String(edit[f.id] ?? f.suggestedMessage ?? '').trim();
    let messageId = null;
    try {
      // The existing staff → borrower path, to the applicant the flag concerns.
      const r = await axios.post(`${API}/api/admin/messages/send`, { user_id: f.applicantUserId || borrowerId, body }, { headers });
      messageId = r?.data?.message?.id || null;
    } catch (e) { setBusy(null); setErr(e?.response?.data?.error || 'The message was not sent.'); return; }
    try { await axios.post(`${API}/api/admin/borrowers/${borrowerId}/credit-rule-flags/${f.id}/sent`, { messageId }, { headers }); await load(); }
    catch (e) { setErr(`The message was sent, but the flag did not record it: ${e?.response?.data?.error || 'try Record again'}.`); setData((d) => d && ({ ...d, unrecorded: { ...(d.unrecorded || {}), [f.id]: messageId } })); }
    setBusy(null);
  };
  const record = async (f, messageId) => {
    setErr(null); setBusy(f.id);
    try { await axios.post(`${API}/api/admin/borrowers/${borrowerId}/credit-rule-flags/${f.id}/sent`, { messageId }, { headers }); await load(); }
    catch (e) { setErr(e?.response?.data?.error || 'Could not record it just now.'); }
    setBusy(null);
  };
  const doDiscard = async (f) => {
    setErr(null); setBusy(f.id);
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/credit-rule-flags/${f.id}/discard`, { reason: discard[f.id] }, { headers });
      setDiscard((d) => { const n = { ...d }; delete n[f.id]; return n; }); await load();
    } catch (e) { setErr(e?.response?.data?.error || 'Could not discard it just now.'); }
    setBusy(null);
  };

  if (!data || !data.available || !(data.flags || []).length) return err ? <div role="alert" style={{ ...card(), fontSize: 14 }}>{err}</div> : null;
  const open = data.flags.filter((f) => f.status === 'open').length;
  return (
    <div data-testid="uw-credit-rule-flags" style={{ ...card({ gap: 14 }), fontFamily: T.font, color: T.ink }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Credit flags ({open} open)</h2>
        <div style={label}>Staff only · rule set {data.ruleSetVersion}</div>
      </div>
      <div style={{ fontSize: 13 }}>Flags never decline, hold or message. Send a follow-up to the applicant, or discard it with a reason.</div>
      {err && <div role="alert" style={{ fontSize: 14, fontWeight: 700 }}>{err}</div>}
      {data.flags.map((f) => {
        const unrecorded = data.unrecorded && data.unrecorded[f.id];
        return (
          <div key={f.id} data-testid="uw-credit-rule-flag" data-rule={f.ruleKey} data-status={f.status}
            style={{ borderTop: `1px solid ${T.line}`, paddingTop: 12, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <b data-testid="uw-credit-rule-flag-cite" style={{ fontSize: 14 }}>{f.cite}</b>
              <span data-testid="uw-credit-rule-flag-status" style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 9999,
                background: f.status === 'open' ? T.tan : f.status === 'sent' ? T.green : T.slateLight, color: T.ink }}>{STATUS_LABEL[f.suppressed ? 'suppressed' : f.status] || f.status}</span>
            </div>
            <div data-testid="uw-credit-rule-flag-finding" style={{ fontSize: 14, lineHeight: 1.6 }}>{f.finding}</div>
            {f.ruleText && <div style={{ fontSize: 13 }}><b>Rule:</b> {f.ruleText}</div>}
            <ResidualWorking r={f.residual} />
            {f.noLetter && (
              <div data-testid="uw-credit-rule-flag-no-letter" style={{ fontSize: 13 }}><b>No letter is offered:</b> this is an automatic decline.</div>
            )}
            {f.status === 'open' && !f.followUpHeld && (f.noLetter ? null :
              <>
                <label style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>Suggested follow-up{f.applicantRole === 'coborrower' ? ' (to the co-borrower)' : ''}
                  <textarea data-testid="uw-credit-rule-flag-message" rows={4} value={edit[f.id] ?? f.suggestedMessage ?? ''}
                    onChange={(e) => setEdit((x) => ({ ...x, [f.id]: e.target.value }))} style={{ ...input, fontWeight: 500 }} />
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {unrecorded
                    ? <button type="button" data-testid="uw-credit-rule-flag-record" disabled={busy === f.id} onClick={() => record(f, unrecorded)} style={btn('primary', busy === f.id)}>Record the sent message</button>
                    : <button type="button" data-testid="uw-credit-rule-flag-send" disabled={busy === f.id || !String(edit[f.id] ?? f.suggestedMessage ?? '').trim()}
                      onClick={() => send(f)} style={btn('primary', busy === f.id)}>Send to the applicant</button>}
                  {!unrecorded && discard[f.id] == null && (
                    <button type="button" data-testid="uw-credit-rule-flag-discard" onClick={() => setDiscard((d) => ({ ...d, [f.id]: '' }))} style={btn()}>Discard</button>
                  )}
                </div>
                {discard[f.id] != null && (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <textarea data-testid="uw-credit-rule-flag-discard-reason" rows={2} placeholder="Why it is not being sent (at least 10 characters)"
                      value={discard[f.id]} onChange={(e) => setDiscard((d) => ({ ...d, [f.id]: e.target.value }))} style={input} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" data-testid="uw-credit-rule-flag-discard-confirm" disabled={busy === f.id || String(discard[f.id]).trim().length < 10}
                        onClick={() => doDiscard(f)} style={btn('primary', busy === f.id || String(discard[f.id]).trim().length < 10)}>Discard the follow-up</button>
                      <button type="button" onClick={() => setDiscard((d) => { const n = { ...d }; delete n[f.id]; return n; })} style={btn()}>Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}
            {f.status === 'sent' && <div data-testid="uw-credit-rule-flag-sent" style={{ fontSize: 13 }}><b>Sent:</b> {f.sentBody}</div>}
            {f.status === 'discarded' && <div data-testid="uw-credit-rule-flag-discarded" style={{ fontSize: 13 }}><b>{f.suppressed ? 'Suppressed:' : 'Discarded:'}</b> {f.discardReason}</div>}
          </div>
        );
      })}
    </div>
  );
}
